import { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Grid, SearchBar, SearchContext, SearchContextManager } from "@giphy/react-components";
import type { IGif } from "@giphy/js-types";
import { useIsMobile } from "../../hooks/useIsMobile";

const GIPHY_API_KEY = (import.meta.env.VITE_GIPHY_API_KEY as string | undefined) ?? "";

const PICKER_HEIGHT = 420;
const PICKER_MARGIN = 8;

interface ChatGifPickerProps {
  onSelect: (url: string) => void;
}

interface PickerPos {
  top: number;
  left: number;
}

function GiphyGrid({ width, onGifClick }: { width: number; onGifClick: (gif: IGif) => void }) {
  const { fetchGifs, searchKey } = useContext(SearchContext);
  return (
    <Grid
      key={searchKey}
      width={width}
      columns={width < 300 ? 2 : 3}
      gutter={6}
      fetchGifs={fetchGifs}
      onGifClick={(gif, e) => {
        e.preventDefault();
        onGifClick(gif);
      }}
      hideAttribution
      noLink
    />
  );
}

function ChatGifPicker({ onSelect }: ChatGifPickerProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [pos, setPos] = useState<PickerPos | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const width = isMobile ? 280 : 320;

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceAbove >= PICKER_HEIGHT + PICKER_MARGIN || spaceAbove > spaceBelow;
    const top = placeAbove
      ? Math.max(PICKER_MARGIN, rect.top - PICKER_HEIGHT - PICKER_MARGIN)
      : Math.min(window.innerHeight - PICKER_HEIGHT - PICKER_MARGIN, rect.bottom + PICKER_MARGIN);
    let left = rect.left;
    if (left + width > window.innerWidth - PICKER_MARGIN) {
      left = window.innerWidth - width - PICKER_MARGIN;
    }
    if (left < PICKER_MARGIN) left = PICKER_MARGIN;
    setPos({ top, left });
  };

  const close = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    const popover = popoverRef.current;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    const onScroll = (e: Event) => {
      const target = e.target as Node | null;
      if (target && popoverRef.current?.contains(target)) return;
      close();
    };
    // Wheel over popover: let the grid scroll natively; only preventDefault when the grid
    // can't absorb the wheel in that direction (so the page underneath doesn't scroll and
    // trigger the outside-scroll close). If the wheel is over the search bar / attribution,
    // redirect it to the grid manually.
    const onWheel = (e: WheelEvent) => {
      const grid = popover?.querySelector<HTMLElement>(".chat-gif-picker-grid");
      if (!grid) return;
      const isOverGrid = grid.contains(e.target as Node);
      const atTop = grid.scrollTop <= 0;
      const atBottom = grid.scrollTop + grid.clientHeight >= grid.scrollHeight - 1;
      if (isOverGrid) {
        const wouldEscape = (e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom);
        if (wouldEscape) e.preventDefault();
      } else {
        e.preventDefault();
        grid.scrollTop += e.deltaY;
      }
    };
    const onResize = () => close();
    document.addEventListener("mousedown", onClick);
    document.addEventListener("scroll", onScroll, true);
    popover?.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("scroll", onScroll, true);
      popover?.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", onResize);
    };
  }, [pos]);

  const handleGifClick = (gif: IGif) => {
    const url = gif.images?.fixed_height?.url ?? gif.images?.original?.url;
    if (url) onSelect(url);
    close();
  };

  if (!GIPHY_API_KEY) {
    return null;
  }

  return (
    <>
      <button
        ref={triggerRef}
        className="chat-gif-btn"
        type="button"
        onClick={() => (pos ? close() : open())}
        title={t("gif") || "GIF"}
        aria-label={t("gif") || "GIF"}
      >
        <span className="chat-gif-btn-label" aria-hidden="true">GIF</span>
      </button>

      {pos && createPortal(
        // SearchContextManager wraps its children in a styled div; keeping it OUTSIDE the
        // popover means the popover's flex layout has direct access to its three sections,
        // so the grid actually gets a bounded height and overflow-y: auto can scroll.
        <SearchContextManager apiKey={GIPHY_API_KEY} options={{ lang: i18n.language }}>
          <div
            ref={popoverRef}
            className="chat-gif-picker-popover"
            style={{ top: pos.top, left: pos.left, width }}
          >
            <div className="chat-gif-picker-search">
              <SearchBar placeholder={t("searchGifs") || "Search GIFs…"} />
            </div>
            <div className="chat-gif-picker-grid">
              <GiphyGrid width={width - 16} onGifClick={handleGifClick} />
            </div>
            <div className="chat-gif-picker-attribution">Powered by GIPHY</div>
          </div>
        </SearchContextManager>,
        document.body,
      )}
    </>
  );
}

export default ChatGifPicker;
